import { useEffect, useRef, useState } from 'react';
import {
  createIntersectionObserver,
  isIntersectionObserverSupported,
} from '../lib/intersectionObserver';

/**
 * Hook to observe when an element enters the viewport
 * @param options.rootMargin - Margin around root (default: '0px 0px 0px 0px')
 *        https://developer.mozilla.org/en-US/docs/Web/API/IntersectionObserver/rootMargin
 * @param options.fireOnce - Whether to stop observing after first intersection (default: false)
 * @returns [ref, isInViewport, isSupported] - Attach ref to element, isInViewport indicates visibility, isSupported for graceful fallback handling
 *
 * @example
 * const [ref, isVisible] = useIntersectionObserver({ fireOnce: false });
 * return <div ref={ref}>{isVisible ? 'Visible!' : 'Hidden'}</div>;
 */

type Options = {
  rootMargin?: string;
  fireOnce?: boolean;
};

export function useIntersectionObserver<ElementType extends HTMLElement>(
  options: Options = {},
): [React.RefObject<ElementType>, boolean, boolean] {
  const ref = useRef<ElementType>(null);
  const [isInViewPort, setIsInViewPort] = useState(false);
  const observerRef = useRef<ReturnType<typeof createIntersectionObserver>>();

  const isSupported = isIntersectionObserverSupported();

  useEffect(() => {
    if (!ref.current) return;

    const observer = createIntersectionObserver(options.rootMargin);
    if (!observer) {
      return;
    }

    observerRef.current = observer;

    observer.observe(ref.current, () => setIsInViewPort(true), {
      onNotIntersecting: () => setIsInViewPort(false),
      fireOnce: options.fireOnce ?? false,
    });

    return () => {
      observer.disconnect();
    };
  }, [options.rootMargin, options.fireOnce]);

  return [ref, isInViewPort, isSupported];
}

/**
 * Hook to trigger when scrolling past a specific element
 * @param triggerSelector - CSS selector for the trigger element
 * @param options.rootMargin - Margin around root for early/late triggering
 * @param options.fireOnce - Whether to stop observing after first intersection (default: false)
 * @returns [isInViewport, isSupported] - isInViewport indicates visibility, isSupported for graceful fallback handling
 *
 * @example
 * const showBackToTop = useScrollTrigger('#header', { rootMargin: '-100px' });
 * return showBackToTop && <BackToTopButton />;
 */
export function useScrollTrigger(
  triggerSelector: string | void,
  options: Options = {},
) {
  const [hasScrolledPast, setHasScrolledPast] = useState(false);
  const observerRef = useRef<ReturnType<typeof createIntersectionObserver>>();
  const isSupported = isIntersectionObserverSupported();

  useEffect(() => {
    if (!triggerSelector) {
      return;
    }

    const triggerElement = document.querySelector(
      triggerSelector,
    ) as HTMLElement;

    if (!triggerElement) {
      // eslint-disable-next-line no-console
      console.warn(
        `Scroll trigger element with selector "${triggerSelector}" not found`,
      );
      return;
    }

    const observer = createIntersectionObserver(options.rootMargin);
    if (!observer) {
      return;
    }

    observerRef.current = observer;

    observer.observe(triggerElement, () => setHasScrolledPast(false), {
      onNotIntersecting: () => setHasScrolledPast(true),
      fireOnce: options.fireOnce ?? false,
    });

    return () => {
      observer.disconnect();
    };
  }, [triggerSelector, options.rootMargin, options.fireOnce]);

  return [hasScrolledPast, isSupported];
}
